/**
 * Config "Itau Bank API" (gateway HubFloripa), uma por carteira de cobrança do ERP.
 *
 * ÚNICO lugar com os nomes dos campos JSON trocados com o backend
 * (/secure/bank-gateway/itau/*). Se o backend mudar algum nome, ajuste só aqui.
 *
 * Segredos (Client Secret, certificado e chave) nunca passam pela EasyMarine:
 * esta tela não tem esses campos e o modelo também não.
 */
export interface ItauGatewayConfig {
  id?: number;
  bankCode?: number;
  tenant?: string;
  active?: boolean;

  // Carteira de cobrança do ERP (chave da config junto com marina e banco)
  billingPortfolioId?: number;
  billingPortfolioName?: string;

  // Vínculo com o ERP (igual ao plugin Itaú atual)
  paymentMethod?: number;
  paymentMethodName?: string;
  paymentMethodPix?: number;
  paymentMethodPixName?: string;
  account?: number;
  accountName?: string;

  // Dados da conta no Itaú (exibição, não são segredo).
  // itauClientId só é gravado pela rota do certificado (não vai no POST /config).
  itauClientId?: string;
  branchNumber?: string;
  accountNumber?: string;
  accountNumberVd?: string;
  wallet?: string;
  pixKey?: string;
  beneficiaryDocument?: string;
  beneficiaryName?: string;

  sendFineInterest?: boolean;

  // Preenchidos pelo backend (somente leitura na tela)
  certHolder?: string;
  certValidUntil?: string;
  lastTestOk?: boolean;
  lastTestAt?: string;
  lastTestDetail?: string;
  webhookSubscriptionId?: string;
  bankPaymentNoticeRegistered?: boolean;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** Corpo do POST /secure/bank-gateway/itau/config/{id}/certificate (nomes do contrato). */
export interface ItauGatewayCertificateRequest {
  clientId: string;
  tokenTemporario: string;
  substituir?: boolean;
}

export const ITAU_GATEWAY_BANK_CODE = 341;
export const ITAU_GATEWAY_DEFAULT_WALLET = '109';

/**
 * Situação do pedido de boleto Itaú (gateway) de uma fatura, lida pela tela de faturas
 * (GET /secure/bank-gateway/itau/billet-requests). `lastError` é o último motivo de recusa.
 */
export interface GatewayBilletRequest {
  invoiceId: number;
  attempt?: number;
  status?: GatewayBilletRequestStatus | string;
  protocol?: string;
  lastError?: string;
  lastErrorAt?: string;
  updatedAt?: string;
}

export type GatewayBilletRequestStatus = 'PENDING' | 'ISSUED' | 'REFUSED' | 'CANCELED';

/** No máximo quantas faturas o backend aceita por consulta de billet-requests. */
export const GATEWAY_BILLET_REQUESTS_MAX = 500;

/** Onde, dentro de `data` do envelope {data, status, message}, o backend devolve cada resposta. */
export const ITAU_GATEWAY_ENVELOPE = {
  list: 'configs',
  single: 'config',
  billetRequests: 'billetRequests',
  pdf: 'pdf',
};

/** Campos que a tela envia no POST /config. Os de status ficam com o backend. */
const SAVE_FIELDS: (keyof ItauGatewayConfig)[] = [
  'id',
  'billingPortfolioId',
  'billingPortfolioName',
  'paymentMethod',
  'paymentMethodName',
  'paymentMethodPix',
  'paymentMethodPixName',
  'account',
  'accountName',
  'branchNumber',
  'accountNumber',
  'accountNumberVd',
  'wallet',
  'pixKey',
  'beneficiaryDocument',
  'beneficiaryName',
  'sendFineInterest',
];

export function newItauGatewayConfig(): ItauGatewayConfig {
  return {
    bankCode: ITAU_GATEWAY_BANK_CODE,
    wallet: ITAU_GATEWAY_DEFAULT_WALLET,
    sendFineInterest: true,
    active: false,
  };
}

function trimOrNull(value: any): any {
  if (typeof value !== 'string') {
    return value === undefined ? null : value;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function onlyDigits(value: any): string {
  return value == null ? '' : String(value).replace(/\D/g, '');
}

/**
 * Monta o corpo do POST /config (ItauGatewayConfigInput do backend): só a lista branca,
 * textos aparados e CNPJ só com dígitos.
 */
export function toSavePayload(config: ItauGatewayConfig): ItauGatewayConfig {
  const payload: any = {};
  SAVE_FIELDS.forEach((field) => {
    payload[field] = trimOrNull((config || {})[field]);
  });
  payload.beneficiaryDocument = trimOrNull(onlyDigits(payload.beneficiaryDocument));
  payload.sendFineInterest = (config || {}).sendFineInterest !== false;
  if (payload.id == null) {
    delete payload.id;
  }
  return payload;
}

/** Lê a lista do envelope. Aceita também `data` sendo a própria lista. */
export function readConfigList(response: any): ItauGatewayConfig[] {
  const data = response ? response.data : null;
  const list = Array.isArray(data) ? data : (data ? data[ITAU_GATEWAY_ENVELOPE.list] : null);
  return Array.isArray(list) ? list : [];
}

/** Lê uma config do envelope; null quando o backend não a devolve. */
export function readConfig(response: any): ItauGatewayConfig | null {
  const data = response ? response.data : null;
  const config = data ? data[ITAU_GATEWAY_ENVELOPE.single] : null;
  return config && typeof config === 'object' && !Array.isArray(config) ? config : null;
}

/** Lê data.billetRequests; qualquer outra coisa vira lista vazia. */
export function readBilletRequests(response: any): GatewayBilletRequest[] {
  const data = response ? response.data : null;
  const list = data ? data[ITAU_GATEWAY_ENVELOPE.billetRequests] : null;
  return Array.isArray(list) ? list.filter((r) => r && r.invoiceId != null) : [];
}

/** Lê data.pdf (true = PDF gravado no bucket). */
export function readPdfResult(response: any): boolean {
  const data = response ? response.data : null;
  return !!data && data[ITAU_GATEWAY_ENVELOPE.pdf] === true;
}
